// Dashboard stats, computed from ONE Workflow V2 month load
// (loadWorkflowData). Every population and status rule mirrors the Workflow
// V2 tab the card links to, so the number on a card is exactly the number of
// rows the user sees after clicking it.

import type { WorkflowRow } from '@/pages/WorkflowV2/api';
import { isTruthyFlag } from '@/pages/WorkflowV2/format';
import { rowAssigneeNames } from '@/pages/WorkflowV2/components/AssigneeFilter';
import { isStampedBy } from '@/pages/WorkflowV2/stampedBy';
import {
  emptyQbProcessCounts,
  rowQbProcessMode,
} from '@/pages/WorkflowV2/qbProcess';
import type { QbProcessMode } from '@/pages/WorkflowV2/components/StatusFilterChips';
import {
  ACCOUNT_STATUS_FIELD,
  PAYROLL_STATUS_FIELD,
  SALES_TAX_STATUS_FIELD,
  getStatusOption,
  type TabStatusField,
} from '@/pages/WorkflowV2/config/tabStatus';
import type { AccountStatusOption } from '@/pages/WorkflowV2/config/accountStatus';

/** Workflow V2 tab ids the Dashboard links to (config/workflowTabs.ts) */
export type ServiceTabId = 'bookkeeping' | 'sales-tax' | 'payroll';

/** Per-service-tab population + status/assignee columns */
export const SERVICE_TABS: Record<
  ServiceTabId,
  { serviceColumn: string; statusField: TabStatusField; assigneeColumn: string }
> = {
  bookkeeping: {
    serviceColumn: 'Bookkeeping',
    statusField: ACCOUNT_STATUS_FIELD,
    assigneeColumn: 'Account Manager',
  },
  'sales-tax': {
    serviceColumn: 'Sales Tax',
    statusField: SALES_TAX_STATUS_FIELD,
    assigneeColumn: 'Sales Taxes Assigned To',
  },
  payroll: {
    serviceColumn: 'Payroll',
    statusField: PAYROLL_STATUS_FIELD,
    assigneeColumn: 'Payroll Taxes Assigned To',
  },
};

/** Rows the given Workflow V2 service tab shows (accounts WITH the service) */
export function serviceRows(
  rows: WorkflowRow[],
  tab: ServiceTabId
): WorkflowRow[] {
  const column = SERVICE_TABS[tab].serviceColumn;
  return rows.filter(r => isTruthyFlag(r.data[column]));
}

/** The status value a row holds in a field ('' = No Status), like the
 *  Workflow V2 status chips key it */
function statusValue(field: TabStatusField, row: WorkflowRow): string {
  return getStatusOption(field, row.data[field.column])?.value ?? '';
}

/** Sales Tax / Payroll Status for an account that no longer needs the
 *  work: left out of the cards' totals entirely (not pending, not done) */
export const ACCOUNT_CLOSED = 'Account Closed';

function isClosed(field: TabStatusField, row: WorkflowRow): boolean {
  const raw = row.data[field.column];
  return typeof raw === 'string' && raw.trim() === ACCOUNT_CLOSED;
}

export interface StatusCount {
  option: AccountStatusOption;
  count: number;
}

/**
 * Bookkeeping Status card counts, in the Account Status option order.
 * The `pinned` statuses always render (even at 0) so the row keeps its
 * shape month to month; every other status (No Status, 1099NEC Review, …)
 * renders only when an account actually holds it.
 */
export const PINNED_BOOKKEEPING_STATUSES = [
  'Completed',
  'Working',
  'Special Handling',
  'Ask My Accountant',
  'Awaiting Receipts',
  'Awaiting Receipts & AM For Review',
  'Awaiting AM For Review',
];

export function bookkeepingStatusCounts(rows: WorkflowRow[]): {
  counts: StatusCount[];
  total: number;
} {
  const population = serviceRows(rows, 'bookkeeping');
  const byValue = new Map<string, number>();
  population.forEach(r => {
    const value = statusValue(ACCOUNT_STATUS_FIELD, r);
    byValue.set(value, (byValue.get(value) ?? 0) + 1);
  });
  const pinned = new Set(PINNED_BOOKKEEPING_STATUSES);
  // Pinned statuses first (in their listed order), then the rest in config
  // order — the leading "No Status" option goes last among the extras
  const options = [
    ...PINNED_BOOKKEEPING_STATUSES.map(
      v => ACCOUNT_STATUS_FIELD.options.find(o => o.value === v)!
    ),
    ...ACCOUNT_STATUS_FIELD.options.filter(
      o => !pinned.has(o.value) && o.value !== ''
    ),
    ACCOUNT_STATUS_FIELD.options[0],
  ].filter(Boolean);
  const counts = options
    .map(option => ({ option, count: byValue.get(option.value) ?? 0 }))
    .filter(c => pinned.has(c.option.value) || c.count > 0);
  return { counts, total: population.length };
}

export interface AssigneeProgress {
  /** UPPERCASE employee name (stored casing); null = Unassigned */
  name: string | null;
  total: number;
  complete: number;
  pending: number;
}

/**
 * Per-employee progress for a service tab (Sales Tax / Payroll): accounts
 * WITH the service, grouped by the tab's assignee column, counted by the
 * tab's Pending / Complete status; Account Closed rows are left out.
 * Accounts with no employee assigned are
 * grouped under name = null. Sorted by name; Unassigned last.
 */
export function assigneeProgress(
  rows: WorkflowRow[],
  tab: Exclude<ServiceTabId, 'bookkeeping'>
): { people: AssigneeProgress[]; total: AssigneeProgress } {
  const { statusField, assigneeColumn } = SERVICE_TABS[tab];
  const population = serviceRows(rows, tab).filter(
    r => !isClosed(statusField, r)
  );
  const byName = new Map<string | null, AssigneeProgress>();
  const total: AssigneeProgress = {
    name: null,
    total: 0,
    complete: 0,
    pending: 0,
  };
  const bump = (entry: AssigneeProgress, status: string) => {
    entry.total += 1;
    if (status === 'Complete') entry.complete += 1;
    if (status === 'Pending') entry.pending += 1;
  };
  population.forEach(r => {
    const status = statusValue(statusField, r);
    bump(total, status);
    const names = [...rowAssigneeNames(r.data, [assigneeColumn])];
    (names.length > 0 ? names : [null]).forEach(name => {
      let entry = byName.get(name);
      if (!entry) {
        entry = { name, total: 0, complete: 0, pending: 0 };
        byName.set(name, entry);
      }
      bump(entry, status);
    });
  });
  const people = [...byName.values()].sort((a, b) => {
    if (a.name === null) return 1;
    if (b.name === null) return -1;
    return a.name.localeCompare(b.name);
  });
  return { people, total };
}

/** Percent (0-100, rounded) with a 0 guard */
export function percent(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

/**
 * Link into Workflow V2 on a tab, optionally pre-filtered. See the
 * WorkflowDeepLink note in pages/WorkflowV2/index.tsx for the params.
 */
export function workflowLink(opts: {
  month?: string | null;
  tab?: string;
  status?: string;
  assignee?: string | null;
  /** UPPERCASE name: accounts that employee stamped (stampedBy.ts) */
  stamped?: string | null;
  /** 'ama' = the Ask My Accountant flag chip */
  flag?: 'ama';
  /** Row id whose detail sheet opens */
  open?: string;
}): string {
  const params = new URLSearchParams();
  if (opts.month) params.set('month', opts.month);
  if (opts.tab) params.set('tab', opts.tab);
  if (opts.status !== undefined) params.set('status', opts.status);
  if (opts.assignee) params.set('assignee', opts.assignee);
  if (opts.stamped) params.set('stamped', opts.stamped);
  if (opts.flag) params.set('flag', opts.flag);
  if (opts.open) params.set('open', opts.open);
  const query = params.toString();
  return query ? `/workflow-v2?${query}` : '/workflow-v2';
}

/**
 * The assignee columns that make an account "mine" per section — the same
 * columns Workflow V2's assignee filter matches on that tab, so a My work
 * deep link (assignee=ME) shows the same rows the card counted.
 */
export const MINE_COLUMNS: Record<ServiceTabId, string[]> = {
  bookkeeping: ['Bookkeeper', 'Account Manager'],
  'sales-tax': ['Sales Taxes Assigned To'],
  payroll: ['Payroll Taxes Assigned To'],
};

/**
 * My work for Bookkeeping: the accounts the employee stamped a bookkeeping
 * milestone on for the accounting month (who did the work, not who is
 * assigned: the Account Manager doesn't always download or book)
 */
export function rowsStampedBy(
  rows: WorkflowRow[],
  name: string,
  accountingKey: string
): WorkflowRow[] {
  return rows.filter(r => isStampedBy(r.data, name, accountingKey));
}

/** Rows where the employee (UPPERCASE name) holds one of the columns */
export function rowsAssignedTo(
  rows: WorkflowRow[],
  name: string,
  columns: string[]
): WorkflowRow[] {
  return rows.filter(r => rowAssigneeNames(r.data, columns).has(name));
}

/** True when the employee is assigned anywhere in the month */
export function hasAnyAssignment(rows: WorkflowRow[], name: string): boolean {
  const columns = [...new Set(Object.values(MINE_COLUMNS).flat())];
  return rows.some(r => rowAssigneeNames(r.data, columns).has(name));
}

/** 'YYYY-MM' of the month before (the accounting month a workflow works) */
export function previousMonthKey(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * True when a stamp cell falls in the given 'YYYY-MM'. Stamps are V1's
 * { date, username } objects or plain ISO strings (same rule as Workflow
 * V2's Ready To Book / Booked chips).
 */
export function cellInMonth(value: unknown, monthKey: string): boolean {
  return stampMonth(value) === monthKey;
}

export interface PipelineStage {
  key: string;
  label: string;
  /** Accounts with the stage's stamp for the month (whole team) */
  count: number;
  /** Of those, how many the My work employee stamped (null = team view) */
  mine: number | null;
  /** Accounts that finished the step before this one but not this one
   *  (null = no meaningful gap, e.g. a step not every account needs) */
  waiting: number | null;
  /** The bar's figure (`mine` in My work, else `count`) split by the
   *  account's QB Process: Manual / Online / None */
  byProcess: Record<QbProcessMode, number>;
  /** Short note on what the stage counts, shown under its label */
  description?: string;
}

/**
 * Bookkeeping pipeline for the accounting month: how many Bookkeeping
 * accounts have each completion stamp for that month. Stages are
 * cumulative milestones, not exclusive buckets: an account downloaded and
 * then booked counts in both. With `stampedBy` (an UPPERCASE name), each
 * stage also counts the stamps that employee made.
 *
 * `waiting` is the gap to the step it follows: Statements Booked waits on
 * Statements Retrieved, Prelim review waits on Statements Booked (Manual
 * Receipts Booked is a side step not every account has, so it carries no
 * gap and nothing waits on it), Final review waits on Prelim.
 */
export function bookkeepingPipeline(
  rows: WorkflowRow[],
  accountingKey: string,
  stampedBy: string | null = null
): { stages: PipelineStage[]; total: number } {
  const population = serviceRows(rows, 'bookkeeping');
  const has = (r: WorkflowRow, cols: string[]) =>
    cols.some(c => cellInMonth(r.data[c], accountingKey));
  const stage = (
    key: string,
    label: string,
    cols: string[],
    /** Count of the step this one follows; null = no gap shown */
    follows: number | null,
    description?: string
  ): PipelineStage => {
    const reached = population.filter(r => has(r, cols));
    const shown = stampedBy
      ? population.filter(r =>
          isStampedBy(r.data, stampedBy, accountingKey, cols)
        )
      : reached;
    const byProcess = emptyQbProcessCounts();
    shown.forEach(r => {
      byProcess[rowQbProcessMode(r.data)] += 1;
    });
    return {
      key,
      label,
      description,
      count: reached.length,
      mine: stampedBy ? shown.length : null,
      waiting:
        follows === null || stampedBy
          ? null
          : Math.max(follows - reached.length, 0),
      byProcess,
    };
  };
  const downloaded = stage(
    'downloaded',
    'Statements Retrieved',
    ['Last QB & Statement Downloaded'],
    population.length,
    'Counted once per client. Bank Data counts each bank separately.'
  );
  const booked = stage(
    'booked',
    'Statements Booked',
    ['QB-Bank Statements Booked'],
    downloaded.count
  );
  const receipts = stage(
    'receipts',
    'Manual Receipts Booked',
    ['Manual Receipts Booked'],
    null
  );
  const prelim = stage(
    'prelim',
    'Prelim review',
    ['Prelim Reviewed By Asst Manager'],
    booked.count
  );
  const final = stage(
    'final',
    'Final review',
    ['Reviewed by Account Manager'],
    prelim.count
  );
  return {
    total: population.length,
    stages: [downloaded, booked, receipts, prelim, final],
  };
}

export interface QueueItem {
  rowId: string;
  name: string;
  /** Who it's with (UPPERCASE), when known */
  assignee: string | null;
  /** Secondary detail, e.g. the printout month */
  detail?: string;
}

const businessName = (r: WorkflowRow) => {
  const v = r.data['Legal Business Name'];
  return typeof v === 'string' && v.trim() ? v.trim() : 'Unnamed account';
};

const firstAssignee = (r: WorkflowRow, columns: string[]) =>
  [...rowAssigneeNames(r.data, columns)][0] ?? null;

/** Accounts carrying the Ask My Accountant flag, A–Z */
export function askMyAccountantQueue(rows: WorkflowRow[]): QueueItem[] {
  return rows
    .filter(r => isTruthyFlag(r.data['Ask My Accountant']))
    .map(r => ({
      rowId: r.id,
      name: businessName(r),
      assignee: firstAssignee(r, ['Account Manager']),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** 'YYYY-MM' of a stamp cell ({ date, username } or ISO text), or null */
function stampMonth(value: unknown): string | null {
  let raw: string | null = null;
  if (typeof value === 'string') raw = value;
  else if (value && typeof value === 'object' && !Array.isArray(value)) {
    const date = (value as { date?: unknown }).date;
    if (typeof date === 'string') raw = date;
  }
  return raw && /^\d{4}-\d{2}/.test(raw) ? raw.slice(0, 7) : null;
}

/**
 * Financial statements printed but not yet picked up: the "Fin Stmnts
 * Printed" month is newer than the "Fin Stmnt" picked-up month (or nothing
 * was picked up). Newest printout first.
 */
export function pickupsReady(rows: WorkflowRow[]): QueueItem[] {
  return rows
    .map(r => ({
      r,
      printed: stampMonth(r.data['Last Financial Printout']),
      picked: stampMonth(r.data['Last Customer Pickup']),
    }))
    .filter(({ printed, picked }) => printed && (!picked || picked < printed))
    .sort((a, b) => (b.printed! < a.printed! ? -1 : 1))
    .map(({ r, printed }) => ({
      rowId: r.id,
      name: businessName(r),
      assignee: firstAssignee(r, [
        'Financial Statements Ready For Pickup',
        'Financial Statements Ready for Pick Up',
      ]),
      detail: printed!,
    }));
}

/**
 * Where today sits in the selected workflow month (Central time).
 * 'current' = today is inside it; past/future months have no progress.
 */
export function cycleProgress(
  monthKey: string,
  todayIso: string
): { state: 'past' | 'current' | 'future'; day: number; days: number } {
  const [y, m] = monthKey.split('-').map(Number);
  const days = new Date(y, m, 0).getDate();
  const todayKey = todayIso.slice(0, 7);
  if (todayKey < monthKey) return { state: 'future', day: 0, days };
  if (todayKey > monthKey) return { state: 'past', day: days, days };
  return { state: 'current', day: Number(todayIso.slice(8, 10)), days };
}
